import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import HomeRoute from "./pages/HomeRoute";
import EndUserLayout from "./layouts/EndUserLayout";
import SentryRouteLogger from "./instrumentation/SentryRouteLogger";
import AnalyticsRouteLogger from "./instrumentation/AnalyticsRouteLogger";
import AnalyticsIdentity from "./instrumentation/AnalyticsIdentity";
import Notifications from "./pages/Notifications";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import CustomerLogin from "./pages/CustomerLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCollegeTeams from "./pages/AdminCollegeTeams";
import AdminCollegeTeamDetail from "./pages/AdminCollegeTeamDetail";
import AdminProgramNewsSources from "./pages/AdminProgramNewsSources";
import AdminModelCurrency from "./pages/AdminModelCurrency";
import CustomerDashboard from "./pages/CustomerDashboard";
import LinkStudent from "./pages/LinkStudent";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetupPassword from "./pages/SetupPassword";
import AcceptInvite from "./pages/AcceptInvite";
import { RequireAdmin, RequireCustomer } from "./components/RouteGuards";
import PlayerProfile from "./pages/PlayerProfile";
import Pipeline from "./pages/Pipeline";
import Schools from "./pages/Schools";
import Outreach from "./pages/Outreach";
import CoachCorner from "./pages/CoachCorner";
import Subscribe from "./pages/Subscribe";
import SubscribeSuccess from "./pages/SubscribeSuccess";
import SubscribeCancel from "./pages/SubscribeCancel";
import { CRMLayout } from "./pages/crm/CRMLayout";
import { UserList } from "./pages/crm/UserList";
import { UserDetail } from "./pages/crm/UserDetail";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <SentryRouteLogger />
        <AnalyticsRouteLogger />
        <AnalyticsIdentity />
        <Routes>
          {/* End-user pages share persistent header via layout route */}
          <Route element={<EndUserLayout />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/chat" element={<Index />} />
            <Route path="/dashboard" element={<HomeRoute />} />
            <Route path="/profile" element={<PlayerProfile />} />
            <Route path="/schools" element={<Schools />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/outreach" element={<Outreach />} />
            <Route path="/coach" element={<CoachCorner />} />
          </Route>

          {/* Admin / Customer / Auth routes — independent layouts */}
          <Route path="/recruit-admin" element={<AdminLogin />} />
          <Route
            path="/recruit-admin/dashboard"
            element={
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            }
          />
          <Route
            path="/recruit-admin/college-teams"
            element={
              <RequireAdmin>
                <AdminCollegeTeams />
              </RequireAdmin>
            }
          />
          <Route
            path="/recruit-admin/college-teams/:teamId"
            element={
              <RequireAdmin>
                <AdminCollegeTeamDetail />
              </RequireAdmin>
            }
          />
          <Route
            path="/recruit-admin/program-news"
            element={
              <RequireAdmin>
                <AdminProgramNewsSources />
              </RequireAdmin>
            }
          />
          <Route
            path="/recruit-admin/model-currency"
            element={
              <RequireAdmin>
                <AdminModelCurrency />
              </RequireAdmin>
            }
          />
          <Route
            path="/recruit-admin/crm"
            element={
              <RequireAdmin>
                <CRMLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<UserList />} />
            <Route path="user/:userId" element={<UserDetail />} />
          </Route>
          <Route path="/customer" element={<CustomerLogin />} />
          <Route
            path="/customer/dashboard"
            element={
              <RequireCustomer>
                <CustomerDashboard />
              </RequireCustomer>
            }
          />
          <Route path="/link" element={<LinkStudent />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/subscribe/success" element={<SubscribeSuccess />} />
          <Route path="/subscribe/cancel" element={<SubscribeCancel />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
